import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createProviderConnectionWithWidget } from 'Services/providersConnections';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import Loading from 'Components/Atoms/Loading/Loading';

function loadScript(src, id) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

function removeScript(id) {
    const script = document.getElementById(id);
    if (script) {
        script.remove();
    }
}

const WIDGET_URL = 'https://widget.wealthreader.com/js/load.js';
const WIDGET_ORIGIN = 'https://widget.wealthreader.com';

export default function ProviderConnectionConnectWithWidget(props) {
    const { showUnknownError } = useErrors();

    const [isLoading, setIsLoading] = useState(false);
    const {
        providerConnectionId,
        connectorId,
        connectorCountryCode,
        providerName,
        providerLogoUrl,
        onCancel,
        serviceProviderId,
        onConnect,
    } = props;

    // TO-DO: create real conversion from ISO 3 digits to ISO 2 digits
    let countryCode2Digits = null;
    if (connectorCountryCode === 'AND') {
        countryCode2Digits = 'AD';
    } else {
        countryCode2Digits = connectorCountryCode.slice(0, 2);
    }

    useEffect(() => {
        setIsLoading(true);

        let token;

        const initialize = async () => {
            try {
                token = await createProviderConnectionWithWidget({
                    connectorId,
                    providerConnectionId,
                    name: providerName,
                });
                window.wr_conf = {
                    operation_id: token,
                    default_login: serviceProviderId,
                    default_login_entity_country: countryCode2Digits,
                    wait_full_response: true,
                };

                await loadScript(WIDGET_URL, 'wealthreader-widget');
            } catch (error) {
                showUnknownError(error);
                onCancel();
            } finally {
                setIsLoading(false);
            }
        };

        initialize();

        const handleWidgetMessage = (e) => {
            if (e.origin !== WIDGET_ORIGIN) return;
            if (e.data === 'flow completed') {
                onConnect(token);
            }
        };

        window.addEventListener('message', handleWidgetMessage);

        return () => {
            window.removeEventListener('message', handleWidgetMessage);
            removeScript('wealthreader-widget');
        };
    }, []);

    return (
        <div className="text-center p-6">
            <div className="flex items-center justify-center">
                <img src={providerLogoUrl} alt={providerName} className="w-16 h-auto block" />
                <h2 className="mb-0 text-2xl md:text-3xl text-pretty font-semibold">{providerName}</h2>
            </div>

            <div className="flex items-center justify-center">
                {isLoading && (
                    <div className="py-6 mx-auto w-full">
                        <Loading />
                    </div>
                )}

                <iframe id="wr-iframe" title="Wealth Reader widget" width="100%" referrerPolicy="origin"></iframe>
            </div>
        </div>
    );
}

ProviderConnectionConnectWithWidget.propTypes = {
    connectorId: PropTypes.string.isRequired,
    connectorCountryCode: PropTypes.string.isRequired,
    providerName: PropTypes.string.isRequired,
    providerLogoUrl: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    serviceProviderId: PropTypes.string.isRequired,
    onConnect: PropTypes.func.isRequired,
    providerConnectionId: PropTypes.string,
};
